// src/components/ProofBuilder/index.js
import React, { useRef, useState, useEffect, useCallback } from 'react';
import LiveProof, { resolveField } from '../LiveProof';
import './ProofBuilder.css';

const FONT_OPTIONS = [
  { value: 'CadillacGothic-NarrowRegular', label: 'Narrow Regular' },
  { value: 'CadillacGothic-NarrowMedium',  label: 'Narrow Medium'  },
  { value: 'CadillacGothic-NarrowBold',    label: 'Narrow Bold'    },
  { value: 'CadillacGothic-Regular',       label: 'Regular'        },
  { value: 'CadillacGothic-Medium',        label: 'Medium'         },
  { value: 'CadillacGothic-Bold',          label: 'Bold'           },
  { value: 'CadillacGothic-WideMedium',    label: 'Wide Medium'    },
  { value: 'CadillacGothic-WideBold',      label: 'Wide Bold'      },
];

const FIELD_OPTIONS = [
  { value: 'name',         label: 'Dealership Name' },
  { value: 'salesPhone',   label: 'Sales Phone' },
  { value: 'servicePhone', label: 'Service Phone' },
  { value: 'email',        label: 'Email' },
  { value: 'address',      label: 'Full Address' },
  { value: 'city',         label: 'City' },
  { value: 'website',      label: 'Website URL' },
  { value: 'custom',       label: 'Custom field path…' },
];

const DEFAULT_LAYER = {
  field: 'name',
  customField: '',
  suffix: '',
  top: '50%',
  left: '50%',
  fontSize: 0.04,
  fontFamily: 'CadillacGothic-NarrowRegular',
  color: '#000000',
  textAlign: 'center',
  transform: 'translateX(-50%)',
  coverBg: '#ffffff',
  coverArea: null,
};

const pctToPixel = (pctStr, total) => {
  const val = parseFloat(pctStr);
  return isNaN(val) ? 0 : (val / 100) * total;
};

const clientToPct = (clientX, clientY, rect) => ({
  xPct: Math.max(0, Math.min(100, (clientX - rect.left) / rect.width  * 100)),
  yPct: Math.max(0, Math.min(100, (clientY - rect.top)  / rect.height * 100)),
});

const ProofBuilder = ({ product, dealership, variantImage }) => {
  const containerRef  = useRef(null);
  const [containerWidth,  setContainerWidth]  = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [layers,      setLayers]      = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [copied,      setCopied]      = useState(false);
  const [aspectRatio, setAspectRatio] = useState('auto');

  // null = not dragging; layer index = defining cover area for that layer
  const [coverDragMode, setCoverDragMode] = useState(null);
  const [dragStart,   setDragStart]   = useState(null); // { xPct, yPct }
  const [dragCurrent, setDragCurrent] = useState(null); // { xPct, yPct }

  // ResizeObserver on the canvas
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        setContainerWidth(e.contentRect.width);
        setContainerHeight(e.contentRect.height);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Derive aspect ratio from image natural size
  const handleImageLoad = useCallback((e) => {
    const { naturalWidth: w, naturalHeight: h } = e.target;
    if (w && h) {
      const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
      const d = gcd(w, h);
      setAspectRatio(`${w / d} / ${h / d}`);
    }
  }, []);

  const updateLayer = useCallback((index, changes) =>
    setLayers(prev => prev.map((l, i) => i === index ? { ...l, ...changes } : l)),
  []);

  const removeLayer = useCallback((index) => {
    setCoverDragMode(prev => prev === index ? null : prev);
    setLayers(prev => prev.filter((_, i) => i !== index));
  }, []);

  // ── Normal mode: click canvas to drop a pin ──────────────────────────────
  const handleCanvasClick = useCallback((e) => {
    if (previewMode || coverDragMode !== null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const { xPct, yPct } = clientToPct(e.clientX, e.clientY, rect);
    setLayers(prev => [
      ...prev,
      { ...DEFAULT_LAYER, left: `${xPct.toFixed(1)}%`, top: `${yPct.toFixed(1)}%` },
    ]);
  }, [previewMode, coverDragMode]);

  // ── Cover-drag mode: start on mousedown ─────────────────────────────────
  const handleCanvasMouseDown = useCallback((e) => {
    if (previewMode || coverDragMode === null) return;
    e.preventDefault(); // suppress the subsequent click event
    const rect = containerRef.current.getBoundingClientRect();
    const pos = clientToPct(e.clientX, e.clientY, rect);
    setDragStart(pos);
    setDragCurrent(pos);
  }, [previewMode, coverDragMode]);

  // Window-level mousemove + mouseup so drag works even outside the canvas
  useEffect(() => {
    if (dragStart === null || coverDragMode === null) return;

    const handleMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setDragCurrent(clientToPct(e.clientX, e.clientY, rect));
    };

    const handleUp = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect && dragStart) {
        const end = clientToPct(e.clientX, e.clientY, rect);
        const x1 = Math.min(dragStart.xPct, end.xPct);
        const y1 = Math.min(dragStart.yPct, end.yPct);
        const x2 = Math.max(dragStart.xPct, end.xPct);
        const y2 = Math.max(dragStart.yPct, end.yPct);
        if (x2 - x1 > 1 && y2 - y1 > 1) {
          updateLayer(coverDragMode, {
            coverArea: {
              left:   `${x1.toFixed(1)}%`,
              top:    `${y1.toFixed(1)}%`,
              width:  `${(x2 - x1).toFixed(1)}%`,
              height: `${(y2 - y1).toFixed(1)}%`,
            },
          });
        }
      }
      setCoverDragMode(null);
      setDragStart(null);
      setDragCurrent(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup',   handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup',   handleUp);
    };
  }, [dragStart, coverDragMode, updateLayer]);

  // Escape cancels cover drag
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setCoverDragMode(null);
        setDragStart(null);
        setDragCurrent(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Spec helpers ─────────────────────────────────────────────────────────
  const buildSpec = useCallback(() => ({
    baseImage: null,
    aspectRatio,
    layers: layers.map(({ customField, ...rest }) => ({
      ...rest,
      field: rest.field === 'custom' ? (customField || 'name') : rest.field,
    })),
  }), [layers, aspectRatio]);

  const handleCopySpec = useCallback(() => {
    const pid  = String(product?.id ?? 'PRODUCT_ID');
    const json = `'${pid}': ${JSON.stringify(buildSpec(), null, 2)},`;
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [buildSpec, product]);

  // ── Live drag preview rect ───────────────────────────────────────────────
  const dragPreviewRect = (dragStart && dragCurrent) ? (() => {
    const x1 = Math.min(dragStart.xPct, dragCurrent.xPct);
    const y1 = Math.min(dragStart.yPct, dragCurrent.yPct);
    const x2 = Math.max(dragStart.xPct, dragCurrent.xPct);
    const y2 = Math.max(dragStart.yPct, dragCurrent.yPct);
    return { left: `${x1}%`, top: `${y1}%`, width: `${x2 - x1}%`, height: `${y2 - y1}%` };
  })() : null;

  const inCoverDrag = coverDragMode !== null;

  return (
    <div className="proof-builder">
      <h3 className="proof-builder-title">
        Proof Builder — {product?.name || 'Product'}
      </h3>

      {/* Canvas */}
      <div
        className={[
          'proof-builder-canvas',
          previewMode ? 'proof-builder-canvas--preview'    : '',
          inCoverDrag ? 'proof-builder-canvas--cover-drag' : '',
        ].filter(Boolean).join(' ')}
        ref={containerRef}
        onClick={handleCanvasClick}
        onMouseDown={handleCanvasMouseDown}
      >
        {previewMode ? (
          <LiveProof
            product={product}
            dealership={dealership}
            productId={String(product?.id)}
            specOverride={buildSpec()}
            variantImage={variantImage}
          />
        ) : (
          <>
            <img
              src={variantImage || product?.image}
              alt={product?.name || 'Product'}
              className="proof-builder-img"
              onLoad={handleImageLoad}
              draggable={false}
            />

            {/* Committed cover-area rects */}
            {layers.map((layer, i) =>
              layer.coverArea ? (
                <div
                  key={`cover-${i}`}
                  className={`proof-cover-area-rect${i === coverDragMode ? ' is-active' : ''}`}
                  style={{
                    left:            layer.coverArea.left,
                    top:             layer.coverArea.top,
                    width:           layer.coverArea.width,
                    height:          layer.coverArea.height,
                    backgroundColor: layer.coverBg || 'rgba(255,255,255,0.6)',
                  }}
                />
              ) : null
            )}

            {/* Live drag preview */}
            {dragPreviewRect && (
              <div
                className="proof-cover-drag-preview"
                style={{
                  ...dragPreviewRect,
                  backgroundColor: layers[coverDragMode]?.coverBg || 'rgba(255,255,255,0.5)',
                }}
              />
            )}

            {/* Layer text pins */}
            {containerWidth > 0 && layers.map((layer, i) => (
              <div
                key={`pin-${i}`}
                className="proof-layer-pin"
                style={{
                  left: pctToPixel(layer.left, containerWidth),
                  top:  pctToPixel(layer.top,  containerHeight),
                }}
              >
                {i + 1}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Hint */}
      {!previewMode && (
        <p className={`proof-builder-hint${inCoverDrag ? ' proof-builder-hint--active' : ''}`}>
          {inCoverDrag
            ? `Drag on the image to define the cover rectangle for Layer ${coverDragMode + 1}. Press Esc to cancel.`
            : 'Click the image to place a new text layer.'}
        </p>
      )}

      {/* Layer panels */}
      {!previewMode && layers.length > 0 && (
        <div className="proof-layers-list">
          <h4>Layers</h4>
          {layers.map((layer, i) => (
            <LayerRow
              key={i}
              index={i}
              layer={layer}
              dealership={dealership}
              isDefiningCover={coverDragMode === i}
              onChange={changes => updateLayer(i, changes)}
              onRemove={() => removeLayer(i)}
              onStartCoverDrag={() => setCoverDragMode(prev => prev === i ? null : i)}
              onClearCoverArea={() => updateLayer(i, { coverArea: null })}
            />
          ))}
        </div>
      )}

      {!previewMode && (
        <button
          className="proof-add-layer-btn"
          onClick={() => setLayers(prev => [...prev, { ...DEFAULT_LAYER }])}
        >
          + Add Layer at Center
        </button>
      )}

      <div className="proof-builder-actions">
        <button
          className="proof-action-btn proof-action-btn--preview"
          onClick={() => { setPreviewMode(p => !p); setCoverDragMode(null); }}
        >
          {previewMode ? 'Back to Builder' : 'Preview with Real Dealer Data'}
        </button>
        <button
          className="proof-action-btn proof-action-btn--copy"
          onClick={handleCopySpec}
        >
          {copied ? 'Copied!' : 'Copy Spec JSON to Clipboard'}
        </button>
      </div>

      <p className="proof-builder-meta">
        Aspect ratio: <code>{aspectRatio}</code> · {layers.length} layer{layers.length !== 1 ? 's' : ''}
      </p>
    </div>
  );
};

// ─── Layer Row ────────────────────────────────────────────────────────────────

const LayerRow = ({
  index, layer, dealership, isDefiningCover,
  onChange, onRemove, onStartCoverDrag, onClearCoverArea,
}) => {
  const previewText = resolveField(
    layer.field === 'custom' ? (layer.customField || '') : layer.field,
    dealership,
  ) + (layer.suffix || '');

  const handleNum = (key, val, float = false) => {
    const n = float ? parseFloat(val) : parseInt(val, 10);
    if (!isNaN(n)) onChange({ [key]: n });
  };

  const handlePct = (key, val) => {
    const n = parseFloat(val);
    if (!isNaN(n)) onChange({ [key]: `${n}%` });
  };

  return (
    <div className={`proof-layer-row${isDefiningCover ? ' proof-layer-row--cover-active' : ''}`}>
      <div className="proof-layer-header">
        <span className="proof-layer-index">Layer {index + 1}</span>
        <button className="proof-layer-remove" onClick={onRemove}>✕ Remove</button>
      </div>

      {/* ── Text / position / style fields ── */}
      <div className="proof-layer-fields">

        <label>
          Field
          <select value={layer.field} onChange={e => onChange({ field: e.target.value })}>
            {FIELD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>

        {layer.field === 'custom' && (
          <label>
            Field path
            <input
              type="text"
              value={layer.customField}
              placeholder="e.g. streetAddress"
              onChange={e => onChange({ customField: e.target.value })}
            />
          </label>
        )}

        <label>
          Suffix
          <input
            type="text"
            value={layer.suffix || ''}
            placeholder="e.g. ,"
            maxLength={10}
            onChange={e => onChange({ suffix: e.target.value })}
          />
        </label>

        <div className="proof-layer-preview" style={{ gridColumn: '1 / -1' }}>
          Preview: <em>{previewText || '(empty)'}</em>
        </div>

        <label>
          Left %
          <input type="number" step="0.5" value={parseFloat(layer.left)}
            onChange={e => handlePct('left', e.target.value)} />
        </label>
        <label>
          Top %
          <input type="number" step="0.5" value={parseFloat(layer.top)}
            onChange={e => handlePct('top', e.target.value)} />
        </label>

        <label>
          Font size (% width)
          <input type="number" step="0.005" min="0.005" max="0.3" value={layer.fontSize}
            onChange={e => handleNum('fontSize', e.target.value, true)} />
        </label>

        <label style={{ gridColumn: '1 / -1' }}>
          Font
          <select
            value={layer.fontFamily || 'CadillacGothic-NarrowRegular'}
            onChange={e => onChange({ fontFamily: e.target.value })}
            style={{ fontFamily: layer.fontFamily }}
          >
            {FONT_OPTIONS.map(o => (
              <option key={o.value} value={o.value} style={{ fontFamily: o.value }}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          Text color
          <input type="color" value={layer.color}
            onChange={e => onChange({ color: e.target.value })} />
        </label>

        <label>
          Align
          <select value={layer.textAlign} onChange={e => onChange({ textAlign: e.target.value })}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </label>

      </div>{/* end .proof-layer-fields */}

      {/* ── Cover area ── always visible, outside the grid ── */}
      <div className="proof-cover-section">
        <div className="proof-cover-section-header">
          <span className="proof-cover-section-title">Cover Area</span>
          <label className="proof-cover-color-row">
            BG color
            <input type="color" value={layer.coverBg || '#ffffff'}
              onChange={e => onChange({ coverBg: e.target.value })} />
          </label>
        </div>

        <div className="proof-cover-section-body">
          {layer.coverArea ? (
            <>
              <span className="proof-cover-dims">
                L {layer.coverArea.left} &nbsp;·&nbsp;
                T {layer.coverArea.top} &nbsp;·&nbsp;
                W {layer.coverArea.width} &nbsp;·&nbsp;
                H {layer.coverArea.height}
              </span>
              <div className="proof-cover-btns">
                <button
                  className={`proof-drag-cover-btn${isDefiningCover ? ' is-active' : ''}`}
                  type="button" onClick={onStartCoverDrag}
                >
                  {isDefiningCover ? 'Cancel (Esc)' : 'Redraw'}
                </button>
                <button className="proof-clear-btn" type="button" onClick={onClearCoverArea}>
                  Clear
                </button>
              </div>
            </>
          ) : (
            <button
              className={`proof-drag-cover-btn${isDefiningCover ? ' is-active' : ''}`}
              type="button" onClick={onStartCoverDrag}
            >
              {isDefiningCover
                ? 'Drag on image now… (Esc to cancel)'
                : 'Drag Cover Area on Image'}
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default ProofBuilder;
